// src/pages/NewHome.jsx
// Piano With Pan V1.2 — Landing Page (Premium)
// ✅ Premium visual upgrade: gradient hero, animated notes, glassmorphic card
// ✅ About/Info modal for parents & new users (creator bio, app explanation)
// ✅ START GAME plays video → SKIP VIDEO / CONTINUE → /learn-your-notes
// ✅ Fredoka One + Nunito fonts for brand consistency with PracticeMode/LearnYourNotes
// ✅ All original video + auth logic preserved

import React, {
  useRef, useState, useEffect, useCallback, useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import InstallAppButton from "../components/InstallAppButton";

/* ─────────────────────────────────────────────
   ASSETS
   ───────────────────────────────────────────── */
const MAIN_VIDEO_NAME = "Treble Town Landing Page Video.mp4";
const ENCODED_MAIN    = "/videos/" + encodeURIComponent(MAIN_VIDEO_NAME);
const RAW_MAIN        = "/videos/" + MAIN_VIDEO_NAME;
const TOWN_LEFT_IMG   = "/images/bg/town-left.png";
const TOWN_RIGHT_IMG  = "/images/bg/town-right.png";
const NAV_LOGO_SRC    = "/icons/pwp-192.png";
const VIDEO_POSTER    = "/icons/pwp-512.png";
const CAPTIONS_VTT    = "";

/* ─────────────────────────────────────────────
   SCENERY  (unchanged from v1.1)
   ───────────────────────────────────────────── */
function Scenery() {
  const notes = Array.from({ length: 12 }, (_, i) => ({
    left:     `${8 + i * 7}%`,
    delay:    `${i * 0.6}s`,
    duration: `${8 + (i % 4)}s`,
    size:     18 + (i % 3) * 6,
    char:     i % 2 ? "♪" : "♫",
  }));
  return (
    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-sky-200 via-sky-100 to-amber-50" />
      <svg className="absolute top-[-10%] left-0 w-full h-[50%]"
        viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden>
        <defs><filter id="blur" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3"/></filter></defs>
        <g filter="url(#blur)" fill="white" opacity="0.45">
          <ellipse cx="10" cy="18" rx="10" ry="6"/>
          <ellipse cx="24" cy="16" rx="12" ry="7"/>
          <ellipse cx="43" cy="14" rx="14" ry="8"/>
          <ellipse cx="65" cy="17" rx="12" ry="7"/>
          <ellipse cx="84" cy="15" rx="10" ry="6"/>
        </g>
      </svg>
      <img src={TOWN_LEFT_IMG} alt=""
        onError={e=>e.currentTarget.style.display="none"}
        className="absolute bottom-0 left-0 max-w-[28vw] w-[28vw] min-w-[180px] select-none"/>
      <img src={TOWN_RIGHT_IMG} alt=""
        onError={e=>e.currentTarget.style.display="none"}
        className="absolute bottom-0 right-0 max-w-[28vw] w-[28vw] min-w-[180px] select-none"/>
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-emerald-100/70 to-transparent"/>
      <style>{`
        @keyframes tt-float-up {
          0%   { transform:translateY(20vh) translateX(0);    opacity:0; }
          10%  { opacity:0.9; }
          50%  { transform:translateY(-10vh) translateX(6px); }
          100% { transform:translateY(-30vh) translateX(-6px); opacity:0; }
        }
      `}</style>
      {notes.map((n,i)=>(
        <span key={i} style={{left:n.left,bottom:"-6vh",
          animation:`tt-float-up ${n.duration} linear ${n.delay} infinite`,
          fontSize:n.size}}
          className="absolute text-indigo-700/60" aria-hidden>{n.char}</span>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   BIG PLAY ICON
   ───────────────────────────────────────────── */
function BigPlayIcon() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M8 5v14l11-7L8 5z" fill="black"/>
    </svg>
  );
}

/* ─────────────────────────────────────────────
   ABOUT MODAL
   Full parent/user explainer — Treble Town story,
   mic listening, virtual piano, creator bio
   ───────────────────────────────────────────── */
function AboutModal({ open, onClose }) {
  if (!open) return null;

  const features = [
    {
      emoji:"🎹",
      title:"Virtual Piano Built In",
      desc:"No instrument? No problem. A full virtual keyboard is always on screen — just click or tap to play.",
    },
    {
      emoji:"🎤",
      title:"Listens to Your Real Piano",
      desc:"Turn the mic on and Piano With Pan hears your instrument in real time, detecting every note you play.",
    },
    {
      emoji:"🐉",
      title:"The Treble Town Adventure",
      desc:"A hungry dragon named Doron is attacking Treble Town! Your child plays 10 song-levels to put him to sleep and save the inhabitants.",
    },
    {
      emoji:"⭐",
      title:"10 Levels · 10 Songs",
      desc:"Twinkle Twinkle, Ode to Joy, Jingle Bells, Happy Birthday and more. Each level unlocks a star character on their Star Quest.",
    },
    {
      emoji:"🏆",
      title:"Learns Independently",
      desc:"Visual note prompts, key glow hints and a bouncing ball guide the child — no parent needed to sit alongside.",
    },
    {
      emoji:"📈",
      title:"Built-in Progress Tracking",
      desc:"Streaks, accuracy scores and trophy cabinets keep kids motivated to come back every day.",
    },
  ];

  return (
    <div
      style={{
        position:"fixed",inset:0,zIndex:9999,
        background:"rgba(0,0,0,0.60)",backdropFilter:"blur(8px)",
        display:"flex",alignItems:"center",justifyContent:"center",
        padding:16, overflowY:"auto",
      }}
      onClick={e=>{ if(e.target===e.currentTarget)onClose(); }}
    >
      <div style={{
        background:"white",borderRadius:36,
        maxWidth:780,width:"100%",
        boxShadow:"0 32px 80px rgba(0,0,0,0.30)",
        position:"relative",
        animation:"nhPop .4s cubic-bezier(.34,1.56,.64,1)",
        maxHeight:"90vh",overflowY:"auto",
      }}>

        {/* Close */}
        <button type="button" onClick={onClose} style={{
          position:"sticky",top:16,left:"100%",marginRight:20,float:"right",
          background:"rgba(0,0,0,0.07)",border:"none",borderRadius:"50%",
          width:34,height:34,cursor:"pointer",fontSize:"1rem",
          display:"flex",alignItems:"center",justifyContent:"center",
          flexShrink:0,zIndex:2,
        }}>✕</button>

        <div style={{padding:"36px 28px 32px",clear:"both"}}>

          {/* Header */}
          <div style={{textAlign:"center",marginBottom:28}}>
            <div style={{fontSize:"2.8rem",lineHeight:1,marginBottom:8}}>🎹</div>
            <div style={{
              fontFamily:"'Fredoka One',cursive",
              fontSize:"clamp(1.5rem,5vw,2rem)",
              color:"#1E293B",lineHeight:1.2,
            }}>
              Piano With Pan
            </div>
            <div style={{
              fontWeight:700,color:"#64748B",
              fontSize:"0.88rem",marginTop:6,lineHeight:1.5,
            }}>
              Learn piano by going on a magical adventure —<br/>
              designed for complete beginners aged 5 and up.
            </div>
          </div>

          {/* Big pitch for parents */}
          <div style={{
            background:"linear-gradient(135deg,#EDE9FE,#DDD6FE)",
            borderRadius:24,padding:"20px 22px",marginBottom:24,
          }}>
            <div style={{fontFamily:"'Fredoka One',cursive",fontSize:"1rem",color:"#5B21B6",marginBottom:8}}>
              🌟 For Parents & New Visitors
            </div>
            <p style={{fontWeight:700,color:"#4C1D95",fontSize:"0.88rem",lineHeight:1.65,margin:0}}>
              All you have to do is open this app, hand your child the device, and press{" "}
              <strong>Start Game</strong>. The adventure teaches them piano — note by note,
              song by song — completely on their own. No music theory. No reading sheet music.
              Just play, explore, and save Treble Town from the dragon Doron! 🐉
            </p>
          </div>

          {/* Feature grid */}
          <div style={{
            display:"grid",
            gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",
            gap:12,marginBottom:28,
          }}>
            {features.map(({emoji,title,desc})=>(
              <div key={title} style={{
                background:"#F8FAFC",borderRadius:18,
                padding:"14px 16px",
                border:"1.5px solid #E2E8F0",
              }}>
                <div style={{fontSize:"1.5rem",marginBottom:6}}>{emoji}</div>
                <div style={{fontFamily:"'Fredoka One',cursive",fontSize:"0.95rem",
                  color:"#1E293B",marginBottom:4}}>{title}</div>
                <div style={{fontSize:"0.78rem",fontWeight:600,color:"#64748B",
                  lineHeight:1.5}}>{desc}</div>
              </div>
            ))}
          </div>

          {/* The journey */}
          <div style={{
            background:"linear-gradient(135deg,#F0FDF4,#DCFCE7)",
            borderRadius:24,padding:"20px 22px",marginBottom:24,
          }}>
            <div style={{fontFamily:"'Fredoka One',cursive",fontSize:"1rem",
              color:"#166534",marginBottom:10}}>
              🗺️ The Learning Journey
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,alignItems:"center"}}>
              {[
                "Watch Intro Video",
                "Learn the Notes",
                "Practice Mode",
                "Level 1 – Twinkle Twinkle",
                "Levels 2–9 – 8 More Songs",
                "Level 10 – Defeat Doron!",
              ].map((step,i,arr)=>(
                <React.Fragment key={step}>
                  <span style={{
                    background:"white",border:"1.5px solid #86EFAC",
                    borderRadius:100,padding:"4px 12px",
                    fontWeight:700,fontSize:"0.75rem",color:"#166534",
                    whiteSpace:"nowrap",
                  }}>{step}</span>
                  {i<arr.length-1&&(
                    <span style={{color:"#86EFAC",fontWeight:900,fontSize:"1rem"}}>→</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Creator bio */}
          <div style={{
            background:"linear-gradient(135deg,#FFF7ED,#FEF3C7)",
            borderRadius:24,padding:"20px 22px",
          }}>
            <div style={{fontFamily:"'Fredoka One',cursive",fontSize:"1rem",
              color:"#92400E",marginBottom:10}}>
              👩‍🎓 About the Creator
            </div>
            <p style={{fontWeight:700,color:"#78350F",fontSize:"0.85rem",
              lineHeight:1.7,margin:0}}>
              <strong>Piano With Pan</strong> was created by{" "}
              <strong>Nishat Chadha</strong>, a Music Educator and Music Composer
              based in <strong>Mumbai, India</strong>, with over{" "}
              <strong>10 years of teaching experience</strong> with students across
              the globe. Nishat built Piano With Pan to make piano accessible to every child —
              regardless of whether they own an instrument — by combining years of 
              teaching expertise with an immersive storytelling experience.
               Every note, every level, and every song has been
               carefully chosen to build real piano skills progressively.

            </p>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MENU BAR  (v1.2: About/Info button added)
   ───────────────────────────────────────────── */
function MenuBar({ nav, onAbout }) {
  const { session, loading, signOut } = useAuth();
  const [busy,setBusy] = useState(false);
  const hasSession = Boolean(session);

  const onLogin = () => {
    const back = window.location.pathname+(window.location.search||"");
    nav(`/login?back=${encodeURIComponent(back)}`);
  };
  const onLogout = async () => {
    setBusy(true);
    try { await signOut(); } catch {} finally { setBusy(false); nav("/home",{replace:true}); }
  };

  return (
    <header className="w-full bg-white/90 backdrop-blur border-b border-black/10">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-3">

        {/* Logo + brand — single line on all sizes */}
        <div className="flex items-center gap-2 cursor-pointer select-none"
          onClick={()=>nav("/home")} style={{touchAction:"manipulation"}}>
          <img src={NAV_LOGO_SRC} alt="Piano With Pan"
            className="w-7 h-7 rounded-lg object-contain flex-shrink-0"
            onError={e=>e.currentTarget.style.display="none"}/>
          <div className="text-base sm:text-2xl font-extrabold tracking-tight text-blue-900 whitespace-nowrap"
            style={{fontFamily:"'Fredoka One',cursive"}}>
            Piano With Pan™
          </div>
        </div>

        {/* Desktop nav */}
        <nav className="ml-auto hidden md:flex items-center gap-5 font-semibold">
          <div style={{touchAction:"manipulation"}}><InstallAppButton/></div>
          <button onClick={()=>nav("/pricing")} className="hover:opacity-80 text-sm">PRICING</button>
          <button onClick={()=>nav("/parents")} className="hover:opacity-80 text-sm">PARENTS</button>
          <button onClick={()=>nav("/about")}   className="hover:opacity-80 text-sm">ABOUT&nbsp;US</button>

          {/* ✅ Info button — opens About modal */}
          <button type="button" onClick={onAbout}
            className="px-3 py-1.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 font-extrabold text-xs hover:bg-amber-200 transition"
            style={{touchAction:"manipulation"}}>
            ℹ️ How It Works
          </button>

          {!loading&&hasSession ? (
            <button onClick={onLogout} disabled={busy}
              className="px-4 py-1.5 rounded-full border border-red-700 text-red-800 hover:bg-red-50 disabled:opacity-60"
              style={{touchAction:"manipulation"}}>
              {busy?"LOGGING OUT...":"LOGOUT"}
            </button>
          ) : (
            <button onClick={onLogin}
              className="px-4 py-1.5 rounded-full border border-blue-800 text-blue-900 hover:bg-blue-50"
              style={{touchAction:"manipulation"}}>
              LOG&nbsp;IN
            </button>
          )}
        </nav>

        {/* Mobile auth + info — compact */}
        <div className="md:hidden ml-auto flex items-center gap-2">
          <button type="button" onClick={onAbout}
            className="w-8 h-8 rounded-full bg-amber-100 border border-amber-300 text-sm flex items-center justify-center"
            style={{touchAction:"manipulation"}}>ℹ️</button>
          {!loading&&hasSession ? (
            <button onClick={onLogout} disabled={busy}
              className="px-3 py-1.5 rounded-full border border-red-300 text-red-700 text-xs font-bold"
              style={{touchAction:"manipulation"}}>{busy?"…":"OUT"}</button>
          ) : (
            <button onClick={onLogin}
              className="px-4 py-1.5 rounded-full bg-blue-900 text-white text-xs font-extrabold"
              style={{touchAction:"manipulation"}}>LOG IN</button>
          )}
        </div>
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────────
   FEATURE PILLS  (shown below video before start)
   ───────────────────────────────────────────── */
function FeaturePills() {
  const items = [
    { emoji:"🎹", text:"Play Real Songs" },
    { emoji:"🐉", text:"10 Levels" },
    { emoji:"⭐", text:"Collect Stars" },
    { emoji:"🏆", text:"Win Trophies" },
  ];
  return (
    <div style={{
      display:"flex",gap:8,justifyContent:"center",
      flexWrap:"wrap",marginTop:16,
    }}>
      {items.map(({emoji,text})=>(
        <div key={text} style={{
          background:"rgba(255,255,255,0.85)",
          border:"1.5px solid rgba(255,255,255,0.9)",
          borderRadius:100,padding:"5px 14px",
          display:"flex",alignItems:"center",gap:6,
          fontFamily:"'Nunito',sans-serif",
          fontWeight:800,fontSize:"0.78rem",color:"#334155",
          boxShadow:"0 2px 10px rgba(0,0,0,0.08)",
        }}>
          <span>{emoji}</span>{text}
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
   ───────────────────────────────────────────── */
export default function NewHome() {
  const nav = useNavigate();
  const splashRef    = useRef(null);
  const playerWrapRef= useRef(null);

  const [mainSrc,        setMainSrc       ] = useState(ENCODED_MAIN);
  const [mainVideoFailed,setMainVideoFailed] = useState(false);
  const [viewportWidth,  setViewportWidth ] = useState(
    typeof window!=="undefined"?window.innerWidth:1024
  );
  const [isReady,        setIsReady       ] = useState(false);
  const [hasStarted,     setHasStarted    ] = useState(false);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [showPlayOverlay,setShowPlayOverlay] = useState(true);
  const [inView,         setInView        ] = useState(true);
  // ✅ Auto-show About modal once for first-time visitors
  const [showAbout, setShowAbout] = useState(()=>{
    try {
      const seen = window.localStorage.getItem("pwp_about_seen_v1");
      return !seen; // show if NOT seen before
    } catch {
      return false;
    }
  });

  // Mark as seen when modal closes
  const closeAbout = useCallback(()=>{
    setShowAbout(false);
    try { window.localStorage.setItem("pwp_about_seen_v1","1"); } catch {}
  },[]);

  const hasCaptions = Boolean(CAPTIONS_VTT);

  useEffect(()=>{
    const fn=()=>setViewportWidth(window.innerWidth);
    window.addEventListener("resize",fn);
    return()=>window.removeEventListener("resize",fn);
  },[]);

  const isPhone = viewportWidth < 640;

  const mobileScale = useMemo(()=>{
    const w=viewportWidth;
    if(w>=1024)return 1;
    if(w<350)  return 0.93;
    if(w<390)  return 0.95;
    if(w<430)  return 0.97;
    if(w<520)  return 0.985;
    return 1;
  },[viewportWidth]);

  /* IntersectionObserver — pause video when out of view */
  useEffect(()=>{
    const el=playerWrapRef.current;
    if(!el)return;
    const obs=new IntersectionObserver((entries)=>{
      const visible=Boolean(entries[0]?.isIntersecting);
      setInView(visible);
      if(!visible){
        const v=splashRef.current;
        if(v&&!v.paused){ try{v.pause();}catch{} }
        setShowPlayOverlay(true);
      }
    },{threshold:[0,0.55,1]});
    obs.observe(el);
    return()=>obs.disconnect();
  },[]);

  const nudgeSafariPoster=useCallback(()=>{
    const v=splashRef.current;
    if(!v||hasStarted)return;
    try{
      if(isFinite(v.duration)&&v.duration>0){
        v.currentTime=Math.min(0.05,v.duration/10);
        v.pause();
      }
    }catch{}
  },[hasStarted]);

  const startPlayback=useCallback(async()=>{
    const v=splashRef.current;
    if(!v)return;
    try{
      v.playsInline=true;
      v.setAttribute("playsinline","true");
      v.setAttribute("webkit-playsinline","true");
      v.preload="auto"; v.muted=false; v.defaultMuted=false; v.volume=1;
      const p=v.play();
      if(p&&typeof p.then==="function")await p;
      setHasStarted(true); setShowPlayOverlay(false);
    }catch(e){
      console.warn("Video play failed:",e);
      setShowPlayOverlay(true);
    }
  },[]);

  const handleVideoError=useCallback(()=>{
    if(mainSrc!==RAW_MAIN){ setMainSrc(RAW_MAIN); return; }
    setMainVideoFailed(true);
  },[mainSrc]);

  // ✅ Smart start routing — always goes to /game-menu
  // GameMenu handles all logic: free vs paid, expired pass, level selection
  const getSmartStartPath = useCallback(() => {
    return "/game-menu";
  }, []);

  const handleBottomCta=useCallback(async()=>{
    if(!hasStarted){ await startPlayback(); return; }
    nav(getSmartStartPath());
  },[hasStarted,nav,startPlayback,getSmartStartPath]);

  /* ── Derived layout ── */
  const scaleStyle = !isPhone
    ? { transform:`scale(${mobileScale})`, transformOrigin:"top center" }
    : undefined;

  const containerClass =
    "max-w-6xl mx-auto px-3 sm:px-4 w-full " +
    (isPhone ? "pt-3 pb-6 flex flex-col" : "mt-4 sm:mt-6");

  /* ── CTA label ── */
  const ctaLabel = !hasStarted
    ? "START\u00A0GAME"
    : videoCompleted
    ? "CONTINUE"
    : "SKIP\u00A0VIDEO";

  /* ── Quick-access pill style ── */
  const QUICK_BTN = {
    background: "rgba(255,255,255,0.82)",
    border: "1.5px solid rgba(30,58,138,0.18)",
    borderRadius: 100,
    padding: "6px 16px",
    fontSize: "0.72rem",
    fontWeight: 900,
    color: "#334155",
    cursor: "pointer",
    fontFamily: "'Nunito', sans-serif",
    letterSpacing: "0.03em",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    transition: "background .15s, transform .1s",
    touchAction: "manipulation",
  };

  /* ── CTA style ── */
  const ctaStyle = hasStarted
    ? "rounded-full bg-white/95 border-2 border-indigo-900 shadow-md font-extrabold uppercase hover:brightness-105 active:translate-y-[1px] px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base tracking-[0.14em] text-indigo-900"
    : "nh-cta-btn rounded-full font-extrabold uppercase px-8 sm:px-14 py-4 sm:py-5 text-2xl sm:text-3xl tracking-[0.18em] sm:tracking-[0.28em] w-[92%] max-w-md sm:w-auto";

  /* ══════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#9BE7FF] via-[#FDF7FF] to-[#FFE8C7] text-gray-900 flex flex-col">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@700;800;900&display=swap');
        @keyframes nhPop { from{transform:scale(0.55);opacity:0} to{transform:scale(1);opacity:1} }

        /* ── START GAME button ── */
        .nh-cta-btn {
          background: linear-gradient(180deg, #FFD84D 0%, #FFC200 100%);
          color: #1a1a2e;
          border: 3.5px solid #1e2d6b;
          box-shadow:
            0 8px 0 #b8860b,
            0 12px 32px rgba(255,194,0,0.45),
            inset 0 1px 0 rgba(255,255,255,0.45);
          text-shadow: 0 1px 0 rgba(255,255,255,0.25);
          transition: transform .1s, box-shadow .1s, filter .1s;
          letter-spacing: 0.22em;
          will-change: transform;
        }
        .nh-cta-btn:hover {
          filter: brightness(1.06);
          box-shadow:
            0 10px 0 #b8860b,
            0 16px 40px rgba(255,194,0,0.55),
            inset 0 1px 0 rgba(255,255,255,0.45);
        }
        .nh-cta-btn:active {
          transform: translateY(5px);
          box-shadow:
            0 3px 0 #b8860b,
            0 6px 16px rgba(255,194,0,0.35),
            inset 0 1px 0 rgba(255,255,255,0.35);
        }
        @keyframes nhPulse {
          0%,100% {
            box-shadow:
              0 8px 0 #b8860b,
              0 12px 32px rgba(255,194,0,0.45),
              0 0 0 0 rgba(255,194,0,0.55),
              inset 0 1px 0 rgba(255,255,255,0.45);
          }
          50% {
            box-shadow:
              0 8px 0 #b8860b,
              0 12px 32px rgba(255,194,0,0.45),
              0 0 0 16px rgba(255,194,0,0),
              inset 0 1px 0 rgba(255,255,255,0.45);
          }
        }
      `}</style>

      <MenuBar nav={nav} onAbout={()=>setShowAbout(true)}/>

      {/* About modal */}
      <AboutModal open={showAbout} onClose={closeAbout}/>

      <main className="flex-1 mx-auto w-full relative" style={{ zoom: 0.88 }}>
        <Scenery/>

        <section className="relative flex-1 w-full">
          <div className={containerClass} style={scaleStyle}>

            {/* ── Hero headline — smaller on mobile ── */}
            <div className="w-full max-w-3xl mx-auto text-center mb-2 sm:mb-4">
              <div style={{fontFamily:"'Fredoka One',cursive"}}
                className="text-xl sm:text-3xl font-extrabold tracking-tight text-blue-900 drop-shadow-sm">
                Learn piano like a game 🎹
              </div>
              <div className="mt-1 text-xs sm:text-sm font-semibold text-blue-800/70">
                Play real songs · Collect stars · Defeat Doron 🐉
              </div>
            </div>

            {/* ── Video card ── */}
            <div
              ref={playerWrapRef}
              style={{
                position:"relative",maxWidth:768,margin:"0 auto",
                borderRadius:28,overflow:"hidden",
                boxShadow:"0 24px 60px rgba(0,0,0,0.18),0 6px 18px rgba(0,0,0,0.10)",
                background:"rgba(255,255,255,0.92)",
                border:"2px solid rgba(255,255,255,0.85)",
              }}
            >
              <div className="relative w-full aspect-video bg-slate-900">
                {!mainVideoFailed ? (
                  <>
                    <video
                      ref={splashRef}
                      src={mainSrc}
                      poster={VIDEO_POSTER}
                      className="w-full h-full object-cover"
                      playsInline preload="auto"
                      muted={!hasStarted}
                      controls={hasStarted&&inView}
                      controlsList="nodownload noplaybackrate noremoteplayback"
                      disablePictureInPicture
                      onLoadedMetadata={()=>{ setIsReady(true); nudgeSafariPoster(); }}
                      onCanPlay={()=>setIsReady(true)}
                      onCanPlayThrough={()=>setIsReady(true)}
                      onPlay={()=>{ setShowPlayOverlay(false); setHasStarted(true); }}
                      onPause={()=>setShowPlayOverlay(true)}
                      onEnded={()=>{
                        setShowPlayOverlay(true); setVideoCompleted(true);
                        try{ if(splashRef.current)splashRef.current.currentTime=0; }catch{}
                      }}
                      onError={handleVideoError}
                    >
                      {hasCaptions&&(
                        <track kind="captions" srcLang="en" label="English"
                          src={CAPTIONS_VTT} default={false}/>
                      )}
                    </video>

                    {!isReady&&(
                      <div className="pointer-events-none absolute inset-0 grid place-items-center">
                        <div className="px-4 py-2 rounded-full bg-black/35 text-white text-xs sm:text-sm">
                          Loading…
                        </div>
                      </div>
                    )}

                    {showPlayOverlay&&(
                      <button type="button"
                        onClick={e=>{ e.stopPropagation(); startPlayback(); }}
                        className="absolute inset-0 flex items-center justify-center bg-black/35 backdrop-blur-[1px]"
                        aria-label="Click to play video"
                        style={{touchAction:"manipulation"}}>
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/95 shadow-xl grid place-items-center border border-white">
                            <BigPlayIcon/>
                          </div>
                          <div className="px-4 py-2 rounded-full bg-white/90 text-slate-900 font-extrabold text-xs sm:text-sm shadow border border-white">
                            Click to Play (Sound ON)
                          </div>
                        </div>
                      </button>
                    )}
                  </>
                ) : (
                  /* Video failed fallback */
                  <div className="w-full h-full flex items-center justify-center bg-slate-900">
                    <div className="text-center px-6">
                      <div className="text-4xl mb-3">🎹</div>
                      <div className="text-white font-extrabold text-base sm:text-lg"
                        style={{fontFamily:"'Fredoka One',cursive"}}>
                        Piano With Pan
                      </div>
                      <div className="text-white/70 text-xs mt-1">
                        Save Treble Town from dragon Doron!
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Feature pills — hidden on mobile to keep it clean ── */}
              {!hasStarted&&(
                <div className="hidden sm:block" style={{padding:"12px 16px 16px",background:"rgba(255,255,255,0.92)"}}>
                  <FeaturePills/>
                </div>
              )}
            </div>

            {/* ── How it works nudge — hidden on mobile (ℹ️ in nav handles it) ── */}
            {!hasStarted&&(
              <div className="hidden sm:flex mt-3 justify-center">
                <button type="button" onClick={()=>setShowAbout(true)}
                  className="text-xs sm:text-sm font-extrabold text-blue-800/70 underline underline-offset-3 decoration-dashed hover:text-blue-900"
                  style={{touchAction:"manipulation"}}>
                  ℹ️ How does Piano With Pan work?
                </button>
              </div>
            )}

            {/* ── Main CTA ── */}
            <div className="mt-4 sm:mt-6 flex flex-col items-center gap-2 sm:gap-3">
              <button
                onClick={handleBottomCta}
                className={ctaStyle}
                style={{
                  touchAction:"manipulation",
                  fontFamily:"'Fredoka One',cursive",
                  animation: !hasStarted ? "nhPulse 2.2s ease-in-out infinite" : "none",
                }}
              >
                {ctaLabel}
              </button>

              {/* Sub-label */}
              {!hasStarted ? (
                <div className="text-[11px] sm:text-xs font-semibold text-blue-900/60 text-center">
                  Free to start · No account needed · Works on any device
                </div>
              ) : !videoCompleted ? (
                <div className="text-[11px] sm:text-xs font-semibold text-slate-600 text-center">
                  Skip straight to Level 1 →
                </div>
              ) : (
                <div className="text-[11px] sm:text-xs font-semibold text-slate-600 text-center">
                  Ready? Start the adventure!
                </div>
              )}
            </div>

            {/* ✅ Quick-access row — visible to returning users once video is past/skipped */}
            {hasStarted && (
              <div style={{
                display: "flex", gap: 8, flexWrap: "wrap",
                justifyContent: "center", marginTop: 4,
              }}>
                <button
                  type="button"
                  onClick={() => nav("/treble-town-map")}
                  style={QUICK_BTN}
                >
                  🗺️ Map
                </button>
                <button
                  type="button"
                  onClick={() => nav("/trophy-cabinet")}
                  style={QUICK_BTN}
                >
                  🏆 Trophies
                </button>
                <button
                  type="button"
                  onClick={() => nav("/practice")}
                  style={QUICK_BTN}
                >
                  🎹 Practice
                </button>
              </div>
            )}

          </div>
        </section>
      </main>

      <footer className="py-5 text-center text-xs text-gray-500">
        © {new Date().getFullYear()} Piano With Pan™ · Created by Nishat Chadha · Mumbai, India
      </footer>
    </div>
  );
}